import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

const BLUE = "#2f5fae";
const RED = "#b51f26";

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 34,
    paddingHorizontal: 46,
    fontFamily: "Times-Roman",
    fontSize: 11,
    lineHeight: 1.35,
    color: "#111",
  },

  header: {
    alignItems: "center",
    marginBottom: 18,
  },

  logo: {
    width: 300,
    height: 95,
    objectFit: "contain",
    marginBottom: 4,
  },

  doctorNameHeader: {
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    color: "#1d2f55",
    textTransform: "uppercase",
  },

  specialtyHeader: {
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#1d2f55",
    textTransform: "uppercase",
    marginTop: 2,
  },

  clinicHeader: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9.5,
    marginTop: 3,
  },

  divider: {
    borderBottomWidth: 1.2,
    borderBottomColor: BLUE,
    width: "100%",
    marginTop: 8,
    marginBottom: 6,
  },

  contact: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: BLUE,
    textAlign: "center",
    marginBottom: 2,
  },

  patientBlock: {
    marginTop: 10,
    marginBottom: 18,
  },

  patientRow: {
    flexDirection: "row",
    marginBottom: 4,
  },

  patientLabel: {
    fontFamily: "Times-Bold",
    width: 65,
  },

  patientValue: {
    flex: 1,
  },

  studyBlock: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 18,
  },

  studyLabel: {
    fontFamily: "Times-Bold",
    color: RED,
    fontSize: 12,
    marginRight: 6,
  },

  studyValue: {
    fontFamily: "Times-Bold",
    color: RED,
    fontSize: 12,
    textTransform: "uppercase",
  },

  section: {
    marginBottom: 11,
  },

  sectionTitle: {
    fontFamily: "Times-Bold",
    fontSize: 11,
    marginBottom: 4,
    textTransform: "uppercase",
  },

  paragraph: {
    fontFamily: "Times-Roman",
    fontSize: 11,
    textAlign: "justify",
    marginBottom: 7,
  },

  conclusionBlock: {
    marginTop: 8,
    marginBottom: 18,
  },

  conclusionTitle: {
    fontFamily: "Times-Bold",
    fontSize: 12,
    color: RED,
    marginBottom: 5,
  },

  conclusionText: {
    fontFamily: "Times-Bold",
    fontSize: 11.5,
    color: RED,
    textAlign: "center",
    marginBottom: 4,
  },

  doctorFooter: {
    marginTop: 18,
    width: 250,
  },

  signatureArea: {
    minHeight: 80,
    justifyContent: "flex-end",
  },

  signatureImage: {
    width: 175,
    height: 80,
    objectFit: "contain",
    marginBottom: -4,
  },

  stampImage: {
    width: 155,
    height: 72,
    objectFit: "contain",
    marginTop: -10,
  },

  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#222",
    width: 220,
    marginBottom: 4,
  },

  doctorFooterName: {
    fontFamily: "Times-Bold",
    fontSize: 10.5,
  },

  doctorFooterSpecialty: {
    fontFamily: "Times-Roman",
    fontSize: 10.5,
  },
});

const safe = (v) => String(v || "").trim();

const renderParagraphs = (value) =>
  safe(value)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => (
      <Text key={index} style={styles.paragraph}>
        {line}
      </Text>
    ));

export default function ReportPdfDocument({ data }) {
  const assetBase = window.location.origin;

  const logoSrc = `${assetBase}/curen-logo.jpg`;

  const signatureSrc =
    data.includeSignatureStamp && data.signatureUrl
      ? `${assetBase}${data.signatureUrl.startsWith("/") ? "" : "/"}${data.signatureUrl}`
      : null;

  const stampSrc =
    data.includeSignatureStamp && data.stampUrl
      ? `${assetBase}${data.stampUrl.startsWith("/") ? "" : "/"}${data.stampUrl}`
      : null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logoSrc} style={styles.logo} />

          <Text style={styles.doctorNameHeader}>
            DRA. MONICA SVETLANA ESCOBAR RUIZ
          </Text>

          <Text style={styles.specialtyHeader}>
            RADIOLOGA ULTRASONOGRAFISTA
          </Text>

          <Text style={styles.clinicHeader}>
            CENTRO DE CIRUGIA AMBULATORIA CUREN
          </Text>

          <View style={styles.divider} />

          <Text style={styles.contact}>
            79 avenida norte #422, colonia Escalon, San Salvador
          </Text>

          <Text style={styles.contact}>
            Tel: 2527-4790 | WhatsApp: 7254-4019
          </Text>
        </View>

        <View style={styles.patientBlock}>
          <View style={styles.patientRow}>
            <Text style={styles.patientLabel}>NOMBRE:</Text>
            <Text style={styles.patientValue}>
              {safe(data.patientName)}
            </Text>
          </View>

          {safe(data.documentNumber) ? (
            <View style={styles.patientRow}>
              <Text style={styles.patientLabel}>DUI:</Text>
              <Text style={styles.patientValue}>
                {safe(data.documentNumber)}
              </Text>
            </View>
          ) : null}

          <View style={styles.patientRow}>
            <Text style={styles.patientLabel}>FECHA:</Text>
            <Text style={styles.patientValue}>
              {safe(data.reportDate)}
            </Text>
          </View>
        </View>

        <View style={styles.studyBlock}>
          <Text style={styles.studyLabel}>ESTUDIO:</Text>
          <Text style={styles.studyValue}>
            {safe(data.studyCode)}
          </Text>
        </View>

        {safe(data.indication) ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>INDICACIÓN:</Text>
            {renderParagraphs(data.indication)}
          </View>
        ) : null}

        {safe(data.technique) ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>TÉCNICA:</Text>
            {renderParagraphs(data.technique)}
          </View>
        ) : null}

        <View style={styles.section}>
          {renderParagraphs(data.findings)}
        </View>

        <View style={styles.conclusionBlock}>
          <Text style={styles.conclusionTitle}>CONCLUSIÓN:</Text>

          {safe(data.conclusion)
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line, index) => (
              <Text key={index} style={styles.conclusionText}>
                {line}
              </Text>
            ))}
        </View>

        <View style={styles.doctorFooter}>
          {data.includeSignatureStamp ? (
            <View style={styles.signatureArea}>
              {signatureSrc ? (
                <Image src={signatureSrc} style={styles.signatureImage} />
              ) : null}

              {stampSrc ? (
                <Image src={stampSrc} style={styles.stampImage} />
              ) : null}
            </View>
          ) : (
            <View style={{ height: 55 }} />
          )}

          <View style={styles.signatureLine} />

          <Text style={styles.doctorFooterName}>
            Dra. Monica Svetlana Escobar Ruiz
          </Text>

          <Text style={styles.doctorFooterSpecialty}>
            Radióloga Ultrasonografista
          </Text>
        </View>
      </Page>
    </Document>
  );
}
